import { createDateRange, createScheduleDayParserFromUser, DateRangeType } from "@app/_helpers/utils";
import { ComegoTime, Time, UserSettings } from "@timeghost/web-api";
import { addDays, isBefore, startOfDay, startOfToday } from "date-fns/esm";
import { firstBy } from "thenby";
import { collectItemsInDayRange, indexByStartOfDay } from "./reports-chart-series";
import { ReportItem } from "./utils";

export type SchedulesGraphSeriesBuckets = {
	summed: number[];
	usage: number[];
	missing: number[];
	overtime: number[];
	future: number[];
	absence: number[];
};

export type SchedulesGraphTimeItem = ReportItem<ComegoTime> & {
	userId: string;
	type?: { name?: string };
	duration: number;
};

/** Per-user schedule cap sum over range (empty times -> cap only). */
export function computeUserScheduleCaps(
	users: string[],
	userSettings: UserSettings,
	from: Date,
	to: Date
): Record<string, number> {
	return users.reduce(
		(acc, userId) => {
			const capSum = createDateRange(from, to).reduce((wcc, d) => {
				const dayScheduleParser = createScheduleDayParserFromUser(
					{ ...userSettings, id: userId },
					d,
					undefined,
					true
				);
				if (!dayScheduleParser) return wcc;
				const sched = dayScheduleParser([], d);
				if (!sched?.enabled || !sched.itemEnabled) return wcc;
				return (wcc += sched.cap);
			}, 0);
			acc[userId] = capSum;
			return acc;
		},
		{} as Record<string, number>
	);
}

function emptyPlaceholderTime(date: Date): ReportItem<ComegoTime> {
	return {
		start: date.toISOString(),
		end: date.toISOString(),
		timeDiff: 0,
	} as unknown as ReportItem<ComegoTime>;
}

/**
 * Build stacked schedules graph series buckets from day-indexed times.
 * Pure aggregation; Apex / i18n stay in the page component.
 */
export function aggregateSchedulesGraphSeries(input: {
	times: SchedulesGraphTimeItem[];
	users: string[];
	userSettings: UserSettings;
	seriesRange: Array<[Date, Date, ...unknown[]]>;
	seriesType: DateRangeType;
	userScheduleCap: Record<string, number>;
	startOfPending?: Date;
}): SchedulesGraphSeriesBuckets {
	const { times, userSettings, seriesRange, seriesType, userScheduleCap } = input;
	const startOfPending = input.startOfPending ?? startOfToday();
	const timesByDay = indexByStartOfDay(times, startOfDay);
	const workinghoursEnabled = !!userSettings.workspace.settings?.workinghoursEnabled;
	const schedulesEnabled = !!userSettings.workspace.schedules.workspace.enabled;

	return seriesRange.reduce(
		(seriesData, [date, dateEnd]) => {
			const timeSeries = collectItemsInDayRange(timesByDay, date, dateEnd, startOfDay, addDays, isBefore).sort(
				firstBy((d) => d.start, "asc")
			);

			const userTimeSeries = timeSeries.reduce(
				(acc, r) => {
					if (!acc[r.userId]) return acc;
					acc[r.userId].push(r);
					return acc;
				},
				Object.keys(userScheduleCap).reduce(
					(acc, userId) => {
						if (!acc[userId]) acc[userId] = [emptyPlaceholderTime(date)];
						return acc;
					},
					{} as Record<string, SchedulesGraphTimeItem[]>
				)
			);

			const wdate = startOfDay(date.getTime());
			const timeSeriesParsed = Object.entries(userTimeSeries).reduce(
				(acc, [userid, userTimes]) => {
					const currentUserSettings = { ...userSettings, id: userid };
					const byDay = indexByStartOfDay(userTimes, startOfDay);
					return createDateRange(wdate, dateEnd).reduce((wcc, d) => {
						const userEmploymentDate =
							(currentUserSettings.workspace.users.find((x) => x.id === userid)?.[
								"employment_date"
							] as string) ?? null;
						if (userEmploymentDate && startOfDay(wdate) < startOfDay(new Date(userEmploymentDate))) {
							return wcc;
						}

						const parser = createScheduleDayParserFromUser(currentUserSettings, d, undefined, true);
						const utimes = byDay.get(startOfDay(d).getTime()) ?? [];
						const workTimes = utimes.filter((a) => !a?.type || a?.type?.name === "work");
						let absence = 0;
						if (workinghoursEnabled) {
							absence = utimes.reduce(
								(sum, t) => (t?.type?.name === "absence" ? sum + t.duration : sum),
								0
							);
							wcc.absence += absence;
						}

						let { cap, usage } = parser?.(
							workTimes.map(
								(a) =>
									({
										start: a.start,
										end: a.end,
										timeDiff: a.duration ?? 0,
									}) as unknown as Time
							),
							d
						) ?? {
							cap: 0,
							usage: workTimes.reduce((summed, s) => (summed += s.duration), 0),
						};

						if (!schedulesEnabled) {
							cap = usage + absence;
						}
						const realCap = cap;
						if (cap > 0 && absence > 0) cap -= absence;
						const diffms = Math.floor(~~cap - ~~usage);
						wcc.summed += usage;
						if (absence > realCap) {
							acc.overtime += usage + (absence - realCap);
							wcc.absence -= realCap;
						} else if (diffms < 0) wcc.overtime += Math.abs(diffms);
						else {
							if (d < startOfPending) wcc.missing += diffms;
							else if (d >= startOfPending) acc.future += diffms;
						}
						return wcc;
					}, acc);
				},
				{ missing: 0, overtime: 0, future: 0, summed: 0, absence: 0 }
			);

			if (seriesType !== "day") {
				seriesData.missing.push((timeSeriesParsed.missing - timeSeriesParsed.overtime).clampThis(0.0));
				seriesData.overtime.push((timeSeriesParsed.overtime - timeSeriesParsed.missing).clampThis(0.0));
				seriesData.absence.push(timeSeriesParsed.absence);
			} else {
				seriesData.missing.push(timeSeriesParsed.missing);
				seriesData.overtime.push(timeSeriesParsed.overtime);
				seriesData.absence.push(timeSeriesParsed.absence);
			}
			seriesData.summed.push(timeSeriesParsed.summed);
			seriesData.usage.push((timeSeriesParsed.summed - timeSeriesParsed.overtime).clampThis(0.0));
			seriesData.future.push(timeSeriesParsed.future);
			return seriesData;
		},
		{ summed: [], usage: [], missing: [], overtime: [], future: [], absence: [] } as SchedulesGraphSeriesBuckets
	);
}
